import type { ToastHistoryEntry } from "./toastHistory";

/**
 * One-click PNG export for a single notification, so the user can paste a
 * sharable artifact into a chat/issue instead of taking screenshots.
 *
 * Renders the toast into a canvas (always light theme so it reads cleanly
 * anywhere it lands) and writes the resulting PNG to the browser's default
 * download folder via the standard anchor-click trick. No save dialog, no
 * external dependencies.
 */

function formatTimestamp(ts: number): string {
  return new Date(ts).toLocaleString();
}

function makeFilenameStamp(ts: number): string {
  const d = new Date(ts);
  const pad = (n: number) => String(n).padStart(2, "0");
  return (
    `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}` +
    `_${pad(d.getHours())}${pad(d.getMinutes())}${pad(d.getSeconds())}`
  );
}

function triggerDownload(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.style.display = "none";
  document.body.appendChild(a);
  a.click();
  // Firefox needs a tick before the anchor / object URL go away.
  window.setTimeout(() => {
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }, 200);
}

interface SeverityStyle {
  fill: string;
  glyph: string;
}

function severityStyle(severity: ToastHistoryEntry["severity"]): SeverityStyle {
  switch (severity) {
    case "success":
      return { fill: "#059669", glyph: "✓" };
    case "warning":
      return { fill: "#d97706", glyph: "!" };
    case "error":
      return { fill: "#dc2626", glyph: "✕" };
    case "info":
    default:
      return { fill: "#2563eb", glyph: "i" };
  }
}

function wrapText(
  ctx: CanvasRenderingContext2D,
  text: string,
  maxWidth: number,
): string[] {
  // Honor explicit newlines in the source (Sonner toasts can contain them
  // via the description prop) — wrap each line independently.
  const out: string[] = [];
  for (const paragraph of text.split(/\r?\n/)) {
    if (paragraph === "") {
      out.push("");
      continue;
    }
    const words = paragraph.split(/\s+/);
    let current = "";
    for (const word of words) {
      const candidate = current ? `${current} ${word}` : word;
      if (ctx.measureText(candidate).width <= maxWidth) {
        current = candidate;
      } else {
        if (current) out.push(current);
        current = word;
      }
    }
    if (current) out.push(current);
  }
  return out.length > 0 ? out : [""];
}

export async function downloadAsPng(entry: ToastHistoryEntry): Promise<void> {
  const canvas = document.createElement("canvas");
  const ctx = canvas.getContext("2d");
  if (!ctx) return;

  // Layout constants (logical pixels — we scale x2 below for high-DPI).
  const WIDTH = 600;
  const PADDING = 20;
  const ICON_SIZE = 28;
  const ICON_GAP = 14;
  const MESSAGE_FONT = "14px -apple-system, BlinkMacSystemFont, 'Segoe UI', system-ui, sans-serif";
  const DESC_FONT = "12px -apple-system, BlinkMacSystemFont, 'Segoe UI', system-ui, sans-serif";
  const META_FONT = "11px -apple-system, BlinkMacSystemFont, 'Segoe UI', system-ui, sans-serif";
  const MESSAGE_LH = 20;
  const DESC_LH = 17;
  const META_LH = 16;
  const MAX_LINES_PER_BLOCK = 12; // truncate huge bodies so the image stays sane

  const textX = PADDING + ICON_SIZE + ICON_GAP;
  const textMaxWidth = WIDTH - textX - PADDING;

  // Measure (need a context with font set before measureText is accurate).
  ctx.font = MESSAGE_FONT;
  let messageLines = wrapText(ctx, entry.message, textMaxWidth);
  if (messageLines.length > MAX_LINES_PER_BLOCK) {
    messageLines = [...messageLines.slice(0, MAX_LINES_PER_BLOCK - 1), "…"];
  }
  let descLines: string[] = [];
  if (entry.description) {
    ctx.font = DESC_FONT;
    descLines = wrapText(ctx, entry.description, textMaxWidth);
    if (descLines.length > MAX_LINES_PER_BLOCK) {
      descLines = [...descLines.slice(0, MAX_LINES_PER_BLOCK - 1), "…"];
    }
  }

  const messageBlockH = messageLines.length * MESSAGE_LH;
  const descBlockH = descLines.length > 0 ? descLines.length * DESC_LH + 8 : 0;
  const metaBlockH = META_LH + 4;
  const height = PADDING + Math.max(ICON_SIZE, messageBlockH + descBlockH + metaBlockH) + PADDING;

  // 2x scale for crisper output on retina screens / when zoomed into.
  const dpr = 2;
  canvas.width = WIDTH * dpr;
  canvas.height = height * dpr;
  ctx.scale(dpr, dpr);

  // Card background + soft border.
  ctx.fillStyle = "#ffffff";
  ctx.fillRect(0, 0, WIDTH, height);
  ctx.strokeStyle = "#e5e7eb";
  ctx.lineWidth = 1;
  ctx.strokeRect(0.5, 0.5, WIDTH - 1, height - 1);

  // Severity badge.
  const { fill, glyph } = severityStyle(entry.severity);
  const iconCx = PADDING + ICON_SIZE / 2;
  const iconCy = PADDING + ICON_SIZE / 2;
  ctx.fillStyle = fill;
  ctx.beginPath();
  ctx.arc(iconCx, iconCy, ICON_SIZE / 2, 0, Math.PI * 2);
  ctx.fill();
  ctx.fillStyle = "#ffffff";
  ctx.font = "bold 16px -apple-system, BlinkMacSystemFont, 'Segoe UI', system-ui, sans-serif";
  ctx.textAlign = "center";
  ctx.textBaseline = "middle";
  ctx.fillText(glyph, iconCx, iconCy + 1);

  // Body text.
  ctx.textAlign = "left";
  ctx.textBaseline = "top";
  let y = PADDING;
  ctx.fillStyle = "#0f172a";
  ctx.font = MESSAGE_FONT;
  for (const line of messageLines) {
    ctx.fillText(line, textX, y);
    y += MESSAGE_LH;
  }
  if (descLines.length > 0) {
    y += 4;
    ctx.fillStyle = "#475569";
    ctx.font = DESC_FONT;
    for (const line of descLines) {
      ctx.fillText(line, textX, y);
      y += DESC_LH;
    }
  }
  y += 4;
  ctx.fillStyle = "#94a3b8";
  ctx.font = META_FONT;
  ctx.fillText(
    `${entry.severity.toUpperCase()} · ${formatTimestamp(entry.ts)}`,
    textX,
    y,
  );

  await new Promise<void>((resolve) => {
    canvas.toBlob((blob) => {
      if (blob) {
        triggerDownload(blob, `notification-${makeFilenameStamp(entry.ts)}.png`);
      }
      resolve();
    }, "image/png");
  });
}
